// src/data/userShelters.ts
// ------------------------
// Repository for the `user_shelters` table — the user↔shelter membership rows
// that drive multi-shelter access (session build, login, admin user management).
// Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";
import { cleanEmailFields, cleanEmailFieldsAll, emailIlikePattern, sameEmail } from "./emailFields";
import { updateShelterById } from "./shelters";
import { assertNotZephyrContact, assertNoZephyrContacts } from "./wall";

type UserShelterInsert = Database["public"]["Tables"]["user_shelters"]["Insert"];
type UserShelterUpdate = Database["public"]["Tables"]["user_shelters"]["Update"];

// Every write below runs its row through cleanEmailFields: the contact email is stored
// trimmed + lowercased, blank → NULL, and an invalid address is refused with a friendly
// { error } (email-validation-plan §6B). The wall check then sees the normalised value.
const CONTACT_EMAIL_FIELDS = ["email"] as const;

// The distinct shelter_ids (among the supplied set) that have at least one ACTIVE
// membership. Used by the "Check Primary Contacts" audit to tell a service that has
// contacts-but-no-primary apart from one with no contacts at all. Returns [] for an
// empty input; null on error / no client.
export async function listShelterIdsWithActiveContacts(
  shelterIds: string[]
): Promise<string[] | null> {
  if (!shelterIds || shelterIds.length === 0) return [];
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("user_shelters")
    .select("shelter_id")
    .in("shelter_id", shelterIds)
    .eq("deactivated", false);
  if (error) {
    logger.warn("listShelterIdsWithActiveContacts failed:", error.message);
    return null;
  }
  const rows = (data as unknown as Array<{ shelter_id: string | null }>) ?? [];
  return Array.from(new Set(rows.map((r) => r.shelter_id).filter((x): x is string => !!x)));
}

// All membership rows (admin Users console builds a user_id→codes lookup, and
// the shelter detail's Users tab needs each row's id to mark the Primary contact
// against shelters.primary_contact — contact-model Phase 2). Null on error / no client.
export async function listAllUserShelters(
  // db-189: opts.includeDeactivated (default false) — see the gating note in the body.
  opts?: { includeDeactivated?: boolean }
): Promise<Array<{
  id: string;
  // Contact-model No-Login (db-92): a login-less contact is a membership row with
  // NULL user_id (a real contact — name/email/phone/role/GUID, can be Primary — that
  // has no profiles/auth account).
  user_id: string | null;
  shelter_code: string;
  shelter_id?: string | null;
  is_primary?: boolean;
  // Contact-model Phase 3 (Stage 1 de-wire): is_primary_user is no longer selected —
  // the Primary contact is the shelters.primary_contact pointer. The column drops in
  // Stage 2.
  // Contact-model Phase 3: the per-service CONTACT record lives on the membership.
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  contact_label?: string | null;
  unleashed_contact_guid?: string | null;
  deactivated?: boolean;
  deactivated_at?: string | null;
}> | null> {
  const c = getTypedClient();
  if (!c) return null;
  let q = c
    .from("user_shelters")
    .select(
      "id, user_id, shelter_code, shelter_id, is_primary, first_name, last_name, email, phone, mobile, contact_label, unleashed_contact_guid, deactivated, deactivated_at"
    );
  // db-189: default to ACTIVE contacts only, so every consumer (CRM audience,
  // telemarketing, contacts-import match, invites) is gated for free. The admin
  // Users tab passes includeDeactivated:true for its "Show Deactivated" toggle.
  // (Login users always have deactivated=false — they deactivate via profiles —
  // so this never hides a login row.)
  if (!opts?.includeDeactivated) q = q.eq("deactivated", false);
  // PostgREST silently caps an un-limited select at 1,000 rows (the 1,432-row
  // suppliers bug, 2 Jul 2026). ~641 memberships today, but the Contacts import
  // now recognises existing LOGIN accounts from these rows — a silent truncation
  // would make it re-plan the missing people as new accounts and reset their
  // passwords (finding A6 / A1). Explicit cap, well above any plausible count.
  const { data, error } = await q.limit(5000);
  if (error) {
    logger.warn("listAllUserShelters failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as Array<{
    id: string;
    user_id: string | null;
    shelter_code: string;
    shelter_id?: string | null;
    is_primary?: boolean;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    phone?: string | null;
    mobile?: string | null;
    contact_label?: string | null;
    unleashed_contact_guid?: string | null;
    deactivated?: boolean;
    deactivated_at?: string | null;
  }>;
}

// Memberships for the Link-Contacts GUID backfill (Unleashed Contacts arm): the fields
// the contacts-link planner matches on PLUS unleashed_contact_guid, so already-linked
// rows are skipped. Kept separate from listAllUserShelters (which many screens use and
// doesn't select the GUID). Null on error / no client.
export async function listMembershipsForContactLink(): Promise<Array<{
  id: string;
  shelter_code: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  unleashed_contact_guid: string | null;
}> | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("user_shelters")
    .select("id, shelter_code, first_name, last_name, email, unleashed_contact_guid")
    // db-189: a deactivated contact has a null GUID + no Unleashed contact — skip it.
    .eq("deactivated", false)
    // Explicit cap — PostgREST's silent 1,000-row default would drop the surplus
    // rows from the Link-Contacts plan, reported as neither linked nor unmatched
    // (finding A6).
    .limit(5000);
  if (error) {
    logger.warn("listMembershipsForContactLink failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as Array<{
    id: string;
    shelter_code: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    unleashed_contact_guid: string | null;
  }>;
}

// The GUIDs + code needed to DELETE a membership's Unleashed contact — read BEFORE a
// detach hard-deletes the row (Contacts arm C5-b), so the delete job can carry them.
// customer_guid = the parent shelter's unleashed_guid. Null on error / no client.
export async function getMembershipForContactDelete(membershipId: string): Promise<{
  shelter_code: string | null;
  contact_guid: string | null;
  customer_guid: string | null;
} | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data: mData } = await c
    .from("user_shelters")
    .select("shelter_id, shelter_code, unleashed_contact_guid")
    .eq("id", membershipId)
    .maybeSingle();
  const m = mData as {
    shelter_id: string | null;
    shelter_code: string | null;
    unleashed_contact_guid: string | null;
  } | null;
  if (!m) return null;
  let customerGuid: string | null = null;
  if (m.shelter_id) {
    const { data: shData } = await c
      .from("shelters")
      .select("unleashed_guid")
      .eq("id", m.shelter_id)
      .maybeSingle();
    const sh = shData as { unleashed_guid: string | null } | null;
    customerGuid = sh?.unleashed_guid ?? null;
  }
  return {
    shelter_code: m.shelter_code ?? null,
    contact_guid: m.unleashed_contact_guid ?? null,
    customer_guid: customerGuid,
  };
}

// Membership summaries for a set of ids — resolves the DFV Services Push List's CONTACT
// rows (whose entity_id is a user_shelters.id) to a readable person + service. [] for an
// empty id set; null on error / no client.
export async function listMembershipSummariesByIds(ids: string[]): Promise<Array<{
  id: string;
  shelter_id: string | null;
  shelter_code: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  contact_label: string | null;
  unleashed_contact_guid: string | null;
}> | null> {
  if (!ids || ids.length === 0) return [];
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("user_shelters")
    .select(
      "id, shelter_id, shelter_code, first_name, last_name, email, contact_label, unleashed_contact_guid"
    )
    .in("id", ids);
  if (error) {
    logger.warn("listMembershipSummariesByIds failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as Array<{
    id: string;
    shelter_id: string | null;
    shelter_code: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    contact_label: string | null;
    unleashed_contact_guid: string | null;
  }>;
}

// A user's shelter memberships (session build + login). shelter_id is the live
// key (identity Phase 2); shelter_code is still read for display/fallback while it
// remains an attribute. is_primary marks the default shelter now that
// profiles.shelter_code is retired (docs/retire-profile-shelter-code-plan.md Phase 2).
export async function listUserSheltersByUserId(userId: string): Promise<Array<{
  // id + contact_label added for the caseworker edit save-path (contact-model
  // 3c-iii): it captures the per-service contact_label map and the row ids BEFORE
  // the delete+reinsert so the labels + the Primary-contact pointer survive.
  id?: string;
  shelter_code: string;
  shelter_id?: string | null;
  is_primary?: boolean;
  // Person-level contact name/email — read by the caseworker-edit save path to diff the
  // prior contact against the edited form (Push List inspect modal changes).
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  contact_label?: string | null;
  // Contact-model Phase 3: the per-service CONTACT phone/mobile — the session build
  // sources the new-request phone prefill from here (was profiles.phones).
  phone?: string | null;
  mobile?: string | null;
  // Unleashed Contacts arm: carried through the caseworker-edit delete+reinsert so a
  // save doesn't wipe the linked Contact GUID (which would make the sync duplicate it).
  unleashed_contact_guid?: string | null;
}> | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("user_shelters")
    .select(
      "id, shelter_code, shelter_id, is_primary, first_name, last_name, email, contact_label, phone, mobile, unleashed_contact_guid"
    )
    .eq("user_id", userId);
  if (error) {
    logger.warn("listUserSheltersByUserId failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as Array<{
    id?: string;
    shelter_code: string;
    shelter_id?: string | null;
    is_primary?: boolean;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    contact_label?: string | null;
    phone?: string | null;
    mobile?: string | null;
    unleashed_contact_guid?: string | null;
  }>;
}

// Clear a user's memberships (admin edit replaces them: delete then insert).
export async function deleteUserSheltersByUserId(
  userId: string | undefined
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .delete()
    .eq("user_id", userId ?? "");
  if (error) logger.warn("deleteUserSheltersByUserId failed:", error.message);
  return { error };
}

// Detach ONE membership (user ↔ shelter). Used when deactivating a shelter to
// remove a SHARED worker from this shelter only, leaving their account + other
// shelters intact (docs/account-shelter-lifecycle-plan.md Phase 4).
export async function deleteUserShelter(
  userId: string,
  shelterId: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .delete()
    .eq("user_id", userId)
    .eq("shelter_id", shelterId);
  if (error) logger.warn("deleteUserShelter failed:", error.message);
  return { error };
}

// Set the user's primary shelter: flag is_primary on (userId, shelterCode) and
// clear it on all their other memberships, preserving the "exactly one is_primary
// per user" invariant. Used by the shelter-deactivation cascade repoint (and any
// future admin "change primary" action). is_primary is the source of truth for the
// default shelter (docs/retire-profile-shelter-code-plan.md Phase 3).
export async function setPrimaryShelter(
  userId: string,
  shelterId: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const clear = await c
    .from("user_shelters")
    .update({ is_primary: false } as UserShelterUpdate)
    .eq("user_id", userId);
  if (clear.error) {
    logger.warn("setPrimaryShelter clear failed:", clear.error.message);
    return { error: clear.error };
  }
  const set = await c
    .from("user_shelters")
    .update({ is_primary: true } as UserShelterUpdate)
    .eq("user_id", userId)
    .eq("shelter_id", shelterId);
  if (set.error) logger.warn("setPrimaryShelter set failed:", set.error.message);
  return { error: set.error };
}

// Set the shelter's PRIMARY CONTACT: point shelters.primary_contact at the
// (shelterId, userId) membership row. Contact-model Phase 2 (POINTER-ONLY): the
// shelter's primary contact lives solely on shelters.primary_contact (FK ->
// user_shelters.id); the old is_primary_user flag is RETIRED. Do NOT confuse with
// setPrimaryShelter (is_primary = the user's primary SHELTER / session default). A
// single FK column enforces "one primary contact per shelter" for free — no clear
// step, no partial-unique index. Unleashed's IsDefault is derived from this pointer.
export async function setPrimaryShelterUser(
  shelterId: string,
  userId: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  // Resolve the membership id for (shelter, user). No .maybeSingle so the shared
  // test client (which resolves the awaited builder to a row list) stays happy.
  const { data, error: findErr } = await c
    .from("user_shelters")
    .select("id")
    .eq("shelter_id", shelterId)
    .eq("user_id", userId);
  if (findErr) {
    logger.warn("setPrimaryShelterUser lookup failed:", findErr.message);
    return { error: findErr };
  }
  const rows = data as unknown as Array<{ id: string }> | { id: string } | null;
  const membershipId = Array.isArray(rows) ? (rows[0]?.id ?? null) : (rows?.id ?? null);
  if (!membershipId) return { error: { message: "membership not found" } };
  // Point the shelter at that membership (the shelters repo owns the write).
  return await updateShelterById(shelterId, { primary_contact: membershipId });
}

// Set the shelter's PRIMARY CONTACT by membership id directly (contact-model No-Login):
// the primary can be a login-less contact (user_id NULL), which setPrimaryShelterUser
// can't resolve because it keys on (shelter, user). shelters.primary_contact IS a
// user_shelters.id, so point it straight at the chosen membership. Works for BOTH
// login users and login-less contacts.
export async function setPrimaryContactMembership(
  shelterId: string,
  membershipId: string
): Promise<{ error: { message: string } | null }> {
  return await updateShelterById(shelterId, { primary_contact: membershipId });
}

export async function insertUserShelters(
  rawRows: object[]
): Promise<{ error: { message: string } | null }> {
  const { rows, error: emailError } = cleanEmailFieldsAll(rawRows, CONTACT_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNoZephyrContacts(rows, "insertUserShelters"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("user_shelters").insert(rows as UserShelterInsert[]);
  if (error) logger.warn("insertUserShelters failed:", error.message);
  return { error };
}

// Upsert a single membership (admin create-user). The conflict target stays the
// existing (user_id, shelter_code) unique so this works against the current schema
// with no migrate-before-deploy ordering; Phase 3 switches it to (user_id, shelter_id)
// when shelter_code is dropped. Identity Phase 2: the caller sets shelter_id (the live
// key) on the row too, which is what lets the db-81 fill trigger be retired (db-84).
export async function upsertUserShelter(
  rawRow: object
): Promise<{ error: { message: string } | null }> {
  const { row, error: emailError } = cleanEmailFields(rawRow, CONTACT_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNotZephyrContact(row, "upsertUserShelter"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .upsert(row as UserShelterInsert, { onConflict: "user_id,shelter_code" });
  if (error) logger.warn("upsertUserShelter failed:", error.message);
  return { error };
}

// Insert ONE contact-only membership (user_id NULL — a no-login or Same-Login
// contact from the Contacts import) and RETURN its new id, so the caller can point
// shelters.primary_contact at it via setPrimaryContactMembership when the contact
// is the shelter's Primary. Plain insert (not upsert): a login-less row has no
// (user_id, shelter_code) key to conflict on; re-run idempotency for these is the
// importer's concern (keyed on shelter + name), not the DB (John, 5 Aug).
export async function insertContactMembership(
  rawRow: object
): Promise<{ id: string | null; error: { message: string } | null }> {
  const { row, error: emailError } = cleanEmailFields(rawRow, CONTACT_EMAIL_FIELDS);
  if (emailError) return { id: null, error: emailError };
  assertNotZephyrContact(row, "insertContactMembership"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { id: null, error: null };
  const { data, error } = await c
    .from("user_shelters")
    .insert(row as UserShelterInsert)
    .select("id")
    .single();
  if (error) {
    logger.warn("insertContactMembership failed:", error.message);
    return { id: null, error };
  }
  const r = data as unknown as { id: string } | null;
  return { id: r?.id ?? null, error: null };
}

// True if the shelter already has a PRIMARY CONTACT (shelters.primary_contact set).
// Used when adding/attaching a user so the FIRST user of a service that has no
// Primary yet becomes its Primary — without ever overriding an existing one. On
// error it returns true (fail-safe: assume a Primary exists, so we never clobber one).
// Update the per-service CONTACT fields on a membership (contact-model Phase 3):
// first_name / last_name / email / phone / mobile / contact_label. Keyed on
// (user_id, shelter_id) — the membership grain that maps 1:1 to an Unleashed Contact.
export async function updateMembershipContact(
  userId: string,
  shelterId: string,
  rawPatch: object
): Promise<{ error: { message: string } | null }> {
  const { row: patch, error: emailError } = cleanEmailFields(rawPatch, CONTACT_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNotZephyrContact(patch, "updateMembershipContact"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .update(patch as UserShelterUpdate)
    .eq("user_id", userId)
    .eq("shelter_id", shelterId);
  if (error) logger.warn("updateMembershipContact failed:", error.message);
  return { error };
}

// Update a membership's CONTACT fields by its OWN id (contact-model No-Login): a
// login-less contact has no user_id, so it can't be keyed on (user_id, shelter_id)
// like updateMembershipContact — it's addressed by the user_shelters.id directly.
// Also used to LINK a login-less contact to a freshly-created account (patch = { user_id }).
export async function updateMembershipContactById(
  membershipId: string,
  rawPatch: object
): Promise<{ error: { message: string } | null }> {
  const { row: patch, error: emailError } = cleanEmailFields(rawPatch, CONTACT_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  assertNotZephyrContact(patch, "updateMembershipContactById"); // THE WALL (Layer 3)
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .update(patch as UserShelterUpdate)
    .eq("id", membershipId);
  if (error) logger.warn("updateMembershipContactById failed:", error.message);
  return { error };
}

// Login-less contacts (user_id NULL) with a given email — the SAME person on OTHER
// services (a demoted multi-service login leaves N unlinked login-less rows sharing an
// email). Used to offer "update on their other services too?" when editing one.
export async function listNoLoginContactsByEmail(email: string): Promise<
  Array<{
    id: string;
    shelter_id: string | null;
    shelter_code: string;
    first_name: string | null;
    last_name: string | null;
  }>
> {
  const c = getTypedClient();
  if (!c || !email) return [];
  const { data, error } = await c
    .from("user_shelters")
    .select("id, shelter_id, shelter_code, first_name, last_name, email")
    .is("user_id", null)
    // db-189: fan an edit out onto ACTIVE contacts only — a deactivated same-email
    // row must never be resurrected/overwritten by editing a live one.
    .eq("deactivated", false)
    // Case-insensitive (email-validation-plan §7.2): ILIKE, then an exact re-check.
    .ilike("email", emailIlikePattern(email));
  if (error) {
    logger.warn("listNoLoginContactsByEmail failed:", error.message);
    return [];
  }
  const matches = ((data ?? []) as unknown as Array<{ email: string | null }>).filter((r) =>
    sameEmail(r.email, email)
  );
  return matches as unknown as Array<{
    id: string;
    shelter_id: string | null;
    shelter_code: string;
    first_name: string | null;
    last_name: string | null;
  }>;
}

// Fan a login-less contact's PERSON fields (name/email/phone/mobile — NOT the per-
// service contact_label) onto SPECIFIC login-less rows, addressed by id. Contact-model
// No-Login: the "update on their other DFV Services too" Save-time choice.
// ⚠️ By id, NOT by email — the caller resolves WHICH rows are the same person
// (name+email match), so a shared/generic mailbox can never sweep up a different person
// (the ZephyrQLD122 Donna incident, 30 Aug 2026). See
// docs/contact-fan-out-shared-mailbox-fix-plan.md.
export async function updateNoLoginContactsByIds(
  ids: string[],
  rawPatch: object
): Promise<{ error: { message: string } | null }> {
  const { row: patch, error: emailError } = cleanEmailFields(rawPatch, CONTACT_EMAIL_FIELDS);
  if (emailError) return { error: emailError };
  const c = getTypedClient();
  if (!c || ids.length === 0) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .update(patch as UserShelterUpdate)
    .is("user_id", null)
    .in("id", ids);
  if (error) logger.warn("updateNoLoginContactsByIds failed:", error.message);
  return { error };
}

// db-189 — is this membership currently a service's PRIMARY contact (shelters.primary_contact
// points at it)? Deactivating such a contact is BLOCKED — the operator must repoint the
// Primary first (the db-189 DB trigger enforces the same invariant). Returns true fail-safe on
// error, so an unknown state never lets a Primary be deactivated by accident.
export async function isMembershipPrimaryContact(membershipId: string): Promise<boolean> {
  const c = getTypedClient();
  if (!c || !membershipId) return false;
  const { data, error } = await c
    .from("shelters")
    .select("id")
    .eq("primary_contact", membershipId)
    .limit(1);
  if (error) {
    logger.warn("isMembershipPrimaryContact failed:", error.message);
    return true;
  }
  const rows = data as unknown as Array<{ id: string }> | null;
  return !!rows && rows.length > 0;
}

// db-189 — SOFT-DELETE a login-less contact by id (the no-delete convention). Replaces
// the former hard delete on the "Remove"/"Deactivate" path: keep the row, flag it
// deactivated, stamp deactivated_at, and NULL the Unleashed GUID (its Unleashed contact is
// deleted via the push queue). The db-189 DB trigger refuses this if the contact is still a
// service's shelters.primary_contact, so the caller repoints the Primary first (and surfaces
// the block as a dialog before ever calling this).
export async function deactivateMembershipById(
  membershipId: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .update({
      deactivated: true,
      deactivated_at: new Date().toISOString(),
      unleashed_contact_guid: null,
    } as UserShelterUpdate)
    .eq("id", membershipId);
  if (error) logger.warn("deactivateMembershipById failed:", error.message);
  return { error };
}

// db-189 — REACTIVATE a soft-deleted login-less contact by id: clear the flag +
// deactivated_at. A fresh Unleashed contact (new GUID) is created by the caller enqueuing an
// upsert. The active-scoped unique index (db-189) makes this fail with a unique violation if
// an ACTIVE contact with the same service+name+email already exists — the caller surfaces
// that as a friendly "already an active contact" message, not a raw DB error.
export async function reactivateMembershipById(
  membershipId: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("user_shelters")
    .update({ deactivated: false, deactivated_at: null } as UserShelterUpdate)
    .eq("id", membershipId);
  if (error) logger.warn("reactivateMembershipById failed:", error.message);
  return { error };
}

export async function shelterHasPrimaryUser(
  shelterId: string | null | undefined
): Promise<boolean> {
  const c = getTypedClient();
  if (!c || !shelterId) return false;
  const { data, error } = await c.from("shelters").select("primary_contact").eq("id", shelterId);
  if (error) {
    logger.warn("shelterHasPrimaryUser failed:", error.message);
    return true;
  }
  const rows = data as unknown as Array<{ primary_contact: string | null }> | null;
  return !!rows?.[0]?.primary_contact;
}
